import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { db } from "../../firebase/firebaseConfig";
import { firebaseCache } from "../../utils/firebaseCache";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";

export const fetchClients = createAsyncThunk('clients/fetchClients', async ({ agentId }) => {
  if (!agentId) {
    throw new Error("fetchClients called without agentId");
  }

  console.log("🔍 fetchClients: Starting fetch for agentId:", agentId);

  // Create cache key for all clients of this agent
  const cacheKey = `clients_all_${agentId}`;

  // Try to get from cache first (24 hour TTL - data only changes on writes, handled by invalidation)
  const cachedData = firebaseCache.get(cacheKey);
  if (cachedData) {
    console.log(`✅ Returning ${cachedData.length} clients from cache (key: ${cacheKey})`);
    return cachedData;
  }

  console.log(`📥 Cache miss - fetching clients from Firestore (key: ${cacheKey})`);

  // Debug logging (remove in production)
  if (process.env.NODE_ENV === 'development') {
    console.log("📥 Fetching clients for agent:", agentId);
  }

  try {
    const customerRef = collection(db, 'agents', agentId, 'customers');
    console.log("🔗 fetchClients: Querying Firebase collection:", customerRef.path);
    const snapshot = await getDocs(customerRef);
    const clients = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data(), agentId }));

    console.log("✅ fetchClients: Successfully fetched", clients.length, "clients from Firebase");

    // Cache the result for 24 hours (data only changes on writes, invalidation ensures freshness)
    firebaseCache.set(cacheKey, clients, 24 * 60 * 60 * 1000);

    return clients;
  } catch (error) {
    console.error("❌ fetchClients: Firebase query failed:", error);
    throw error;
  }
});

/**
 * fetchClientsForMultipleUsers - Fetch clients from multiple staff members
 * Used by admins to view aggregated clients from all linked staff
 */
export const fetchClientsForMultipleUsers = createAsyncThunk(
  'clients/fetchClientsForMultipleUsers',
  async (userIds) => {
    if (!userIds || userIds.length === 0) {
      console.log('No user IDs provided, returning empty array');
      return [];
    }

    console.log(`Fetching clients for ${userIds.length} user(s):`, userIds);

    // Create aggregated cache key
    const cacheKey = `clients_aggregated_${userIds.slice().sort().join('_')}`;

    // Try cache first
    const cachedData = firebaseCache.get(cacheKey);
    if (cachedData) {
      console.log(`✅ Returning ${cachedData.length} clients from cache (key: ${cacheKey})`);
      return cachedData;
    }

    // Fetch clients for each user in parallel
    const clientsPromises = userIds.map(async (userId) => {
      try {
        const customerRef = collection(db, 'agents', userId, 'customers');
        const snapshot = await getDocs(customerRef);
        const clients = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          agentId: userId, // Add agentId to track which staff owns this client
        }));
        console.log(`Fetched ${clients.length} clients for user ${userId}`);
        return clients;
      } catch (error) {
        console.error(`Error fetching clients for user ${userId}:`, error);
        return [];
      }
    });

    // Wait for all promises to resolve
    const clientsArrays = await Promise.all(clientsPromises);

    // Flatten the array of arrays into a single array
    const allClients = clientsArrays.flat();

    console.log(`Total clients fetched: ${allClients.length}`);

    // Cache for 24 hours (data only changes on writes, invalidation ensures freshness)
    firebaseCache.set(cacheKey, allClients, 24 * 60 * 60 * 1000);

    return allClients;
  }
);

export const fetchClientById = createAsyncThunk('clients/fetchClientById', async ({ agentId, customerId, forceRefresh = false }) => {
  const cacheKey = `client_${agentId}_${customerId}`;

  // Try to get from cache first (unless forceRefresh is true)
  if (!forceRefresh) {
    const cachedData = firebaseCache.get(cacheKey);
    if (cachedData) {
      return cachedData;
    }
  } else {
    // Clear cache for this specific client when force refreshing
    firebaseCache.clearKey(cacheKey);
    console.log(`🔄 Force refreshing client: ${agentId}/${customerId}`);
  }

  const docRef = doc(db, 'agents', agentId, 'customers', customerId);
  const docSnap = await getDoc(docRef);
  const result = { id: docSnap.id, ...docSnap.data(), agentId };

  // Cache individual client for 24 hours (data only changes on writes, invalidation ensures freshness)
  firebaseCache.set(cacheKey, result, 24 * 60 * 60 * 1000);

  return result;
});

const clientSlice = createSlice({
  name: "clients",
  initialState: {
    clients: [],
    loading: false,
    error: null,
    initialized: false, // Track if data has been loaded at least once
  },
  reducers: {
    clearClientCache: (state, action) => {
      const agentId = action.payload?.agentId;
      firebaseCache.clearClients(agentId);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClients.pending, (state) => {
        // Only show loader if we have no data
        if (state.clients.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.clients = action.payload;
        state.loading = false;
        state.initialized = true; // Mark as initialized after first load
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.initialized = true; // Still mark as initialized to stop total white screen loading
      })
      .addCase(fetchClientsForMultipleUsers.pending, (state) => {
        // Only show loader if we have no data
        if (state.clients.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchClientsForMultipleUsers.fulfilled, (state, action) => {
        state.clients = action.payload;
        state.loading = false;
        state.initialized = true;
      })
      .addCase(fetchClientsForMultipleUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.initialized = true;
      })
      .addCase(fetchClientById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientById.fulfilled, (state, action) => {
        const updatedClient = action.payload;
        const index = state.clients.findIndex((c) => c.id === updatedClient.id);
        if (index !== -1) {
          state.clients[index] = updatedClient;
        } else {
          state.clients.push(updatedClient);
        }
        state.loading = false;
      })
      .addCase(fetchClientById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { clearClientCache } = clientSlice.actions;
export default clientSlice.reducer;